import React, { lazy, Suspense } from 'react';
import { useSelector } from 'react-redux';
import { ProducerSwitch } from 'modules/producers';
import { TalentSwitch } from 'modules/talents';
import { AgentSwitch } from 'modules/agents';
import { ProtectedRoute, ChangePasswordPage } from 'modules/auth';
import { Chat } from 'modules/chat';
import { AccountNavBar } from './components';
import {
  DeleteAccountPage,
  ChangeEmailPage,
  ChangedEmailVerificationPage,
} from './pages';
import { getAccountType } from './redux';
import { AccountType } from './services';

import { Route, Routes, Outlet } from 'react-router-dom';
import { PageLoader } from 'modules/ui';

// Talent route components
import { NotFoundPage } from 'modules/ui';
import { AuditionSearchPage, AuditionPage } from 'modules/auditions';
import { ProfilePage } from 'modules/producers/fragments';
import { OfferPage } from 'modules/offers';
import { AgentPreviewPage } from 'modules/agents';
import {
  EditPersonalPage as TalentEditPersonalPage,
  EditProfessionalPage as TalentEditProfessionalPage,
  EditSamplesPage as TalentEditSamplesPage,
  EditSkillsPage as TalentEditSkillsPage,
  EditAgentsManagersPage as TalentEditAgentsManagersPage,
  ProfilePage as TalentProfilePage,
} from 'modules/talents/fragments';
const SubmissionsPage = lazy(() =>
  import('modules/submissions/fragments/SubmissionsPage'),
);
const OffersPage = lazy(() => import('modules/offers/fragments/OffersPage'));
const GlobalMessagingPage = lazy(() =>
  import('modules/global-messages/pages/GlobalMessagingPage'),
);
const AuditionsMessagingPage = lazy(() =>
  import('modules/audition-messages/pages/AuditionsMessagingPage'),
);

// Agent route components
import { TalentPreviewPage } from 'modules/talents';
import {
  EditPersonalPage as AgentEditPersonalPage,
  EditSocialPage as AgentEditSocialPage,
  EditAgencyPage,
  AgentProfilePage,
  MyClientsPage,
} from 'modules/agents/fragments';
const ListPage = lazy(() => import('modules/lists/fragments/ListPage'));
const ListsPage = lazy(() => import('modules/lists/fragments/ListsPage'));

// Producer route components
import {
  ProfilePage as ProducerProfilePage,
  EditPersonalPage as ProducerEditPersonalPage,
  EditSocialPage as ProducerEditSocialPage,
  SearchPage,
} from 'modules/producers/fragments';
const ProjectRolePage = lazy(() =>
  import('modules/project-roles/fragments/ProjectRolePage'),
);
const ProjectPage = lazy(() =>
  import('modules/projects/fragments/ProjectPage'),
);
const ProjectsPage = lazy(() =>
  import('modules/projects/fragments/ProjectsPage'),
);

export default function AccountSwitch() {
  const accountType = useSelector(getAccountType);

  return (
    <>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route
            path="/"
            element={
              <>
                <AccountNavBar />
                <Outlet />
              </>
            }
          >
            <Route
              path="/verify-changed-email/:email"
              element={
                <ProtectedRoute>
                  <ChangedEmailVerificationPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/change-password"
              element={
                <ProtectedRoute>
                  <ChangePasswordPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/change-email"
              element={
                <ProtectedRoute>
                  <ChangeEmailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/delete-account"
              element={
                <ProtectedRoute>
                  <DeleteAccountPage />
                </ProtectedRoute>
              }
            />
            {/* React Router v6 does not allow custom component rendering inside Routes 
            anymore, so we can't separate these out into separate files and components 
            due to not having a path for a Route that could hold the overarching element. 
            Hoping this is fixed in v7. */}
            {AccountType.isProducer(accountType) && (
              <>
                <Route
                  exact
                  path="/"
                  element={
                    <ProtectedRoute>
                      <ProjectsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/projects/:projectId/roles/:roleId"
                  element={
                    <ProtectedRoute>
                      <ProjectRolePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/projects/:projectId"
                  element={
                    <ProtectedRoute>
                      <ProjectPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/projects"
                  element={
                    <ProtectedRoute>
                      <ProjectsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/lists/:listId"
                  element={
                    <ProtectedRoute>
                      <ListPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/lists/agent-list/:agentListId/:agentId"
                  element={
                    <ProtectedRoute>
                      <ListPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/lists"
                  element={
                    <ProtectedRoute>
                      <ListsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/search"
                  element={
                    <ProtectedRoute>
                      <SearchPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/talent/:talentId"
                  element={
                    <ProtectedRoute>
                      <TalentPreviewPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/agents/:agentId"
                  element={
                    <ProtectedRoute>
                      <AgentPreviewPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <ProducerProfilePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/profile/edit"
                  element={
                    <ProtectedRoute>
                      <ProducerEditPersonalPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/profile/edit/social-media-links"
                  element={
                    <ProtectedRoute>
                      <ProducerEditSocialPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/messaging/auditions/:auditionId/:talentId"
                  element={
                    <ProtectedRoute>
                      <AuditionsMessagingPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/messaging/auditions"
                  element={
                    <ProtectedRoute>
                      <AuditionsMessagingPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/messaging/global/:oppositeAccountType/:oppositeAccountId"
                  element={
                    <ProtectedRoute>
                      <GlobalMessagingPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/messaging/global"
                  element={
                    <ProtectedRoute>
                      <GlobalMessagingPage />
                    </ProtectedRoute>
                  }
                />
                <Route element={<NotFoundPage />} />
              </>
            )}
            {AccountType.isTalent(accountType) && (
              <>
                <Route
                  exact
                  path="/"
                  element={
                    <ProtectedRoute>
                      <AuditionSearchPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/auditions/:auditionId"
                  element={
                    <ProtectedRoute>
                      <AuditionPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/auditions"
                  element={
                    <ProtectedRoute>
                      <AuditionSearchPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/submissions"
                  element={
                    <ProtectedRoute>
                      <SubmissionsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/offers/:offerId"
                  element={
                    <ProtectedRoute>
                      <OfferPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/offers"
                  element={
                    <ProtectedRoute>
                      <OffersPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <TalentProfilePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/talent/:talentId"
                  element={
                    <ProtectedRoute>
                      <TalentProfilePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/profile/edit"
                  element={
                    <ProtectedRoute>
                      <TalentEditPersonalPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/profile/edit/skills"
                  element={
                    <ProtectedRoute>
                      <TalentEditSkillsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/profile/edit/professional-info"
                  element={
                    <ProtectedRoute>
                      <TalentEditProfessionalPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/profile/edit/samples"
                  element={
                    <ProtectedRoute>
                      <TalentEditSamplesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/profile/edit/agents-and-managers"
                  element={
                    <ProtectedRoute>
                      <TalentEditAgentsManagersPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/producers/:producerId"
                  element={
                    <ProtectedRoute>
                      <ProfilePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/agents/:agentId"
                  element={
                    <ProtectedRoute>
                      <AgentPreviewPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/messaging/auditions/:auditionId/:talentId"
                  element={
                    <ProtectedRoute>
                      <AuditionsMessagingPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/messaging/auditions"
                  element={
                    <ProtectedRoute>
                      <AuditionsMessagingPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/messaging/global/:oppositeAccountType/:oppositeAccountId"
                  element={
                    <ProtectedRoute>
                      <GlobalMessagingPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/messaging/global"
                  element={
                    <ProtectedRoute>
                      <GlobalMessagingPage />
                    </ProtectedRoute>
                  }
                />
                <Route element={<NotFoundPage />} />
              </>
            )}
            {AccountType.isAgent(accountType) && (
              <>
                <Route
                  exact
                  path="/"
                  element={
                    <ProtectedRoute>
                      <MyClientsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/auditions/:auditionId"
                  element={
                    <ProtectedRoute>
                      <AuditionPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/auditions"
                  element={
                    <ProtectedRoute>
                      <AuditionSearchPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/profile/edit"
                  element={
                    <ProtectedRoute>
                      <AgentEditPersonalPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/clients"
                  element={
                    <ProtectedRoute>
                      <MyClientsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/lists/:listId"
                  element={
                    <ProtectedRoute>
                      <ListPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/lists"
                  element={
                    <ProtectedRoute>
                      <ListsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/offers"
                  element={
                    <ProtectedRoute>
                      <OffersPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/offers/:offerId"
                  element={
                    <ProtectedRoute>
                      <OfferPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/profile/edit/agency-info"
                  element={
                    <ProtectedRoute>
                      <EditAgencyPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/profile/edit/social-media-links"
                  element={
                    <ProtectedRoute>
                      <AgentEditSocialPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <AgentProfilePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/agents/:agentId"
                  element={
                    <ProtectedRoute>
                      <AgentProfilePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/talent/:talentId"
                  element={
                    <ProtectedRoute>
                      <TalentPreviewPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/producers/:producerId"
                  element={
                    <ProtectedRoute>
                      <ProfilePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/messaging/global/:oppositeAccountType/:oppositeAccountId"
                  element={
                    <ProtectedRoute>
                      <GlobalMessagingPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/messaging/global"
                  element={
                    <ProtectedRoute>
                      <GlobalMessagingPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  element={
                    <ProtectedRoute>
                      <NotFoundPage />
                    </ProtectedRoute>
                  }
                />
              </>
            )}
            <Route
              exact
              path="*"
              element={
                <ProtectedRoute>
                  <NotFoundPage />
                </ProtectedRoute>
              }
            />
          </Route>
        </Routes>
      </Suspense>
      <Chat />
    </>
  );
}
